// Business setup (staff wages by country + GST) and treatment cost logic
import { calculate } from "@/lib/calc";

// Country presets for employment on-costs and GST (all editable in the UI)
export const COUNTRY_PRESETS = {
  AU: {
    label: "Australia", currency: "AUD", gstRate: 10, gstEnabled: true, gstLabel: "GST",
    hoursPerWeek: 38, superPct: 12, workersCompPct: 2, leaveLoadingPct: 17.5,
    annualLeaveWeeks: 4, personalLeaveDays: 10, publicHolidayDays: 11, casualLoadingPct: 25,
  },
  NZ: {
    label: "New Zealand", currency: "NZD", gstRate: 15, gstEnabled: true, gstLabel: "GST",
    hoursPerWeek: 40, superPct: 3, workersCompPct: 1, leaveLoadingPct: 0,
    annualLeaveWeeks: 4, personalLeaveDays: 10, publicHolidayDays: 12, casualLoadingPct: 8,
  },
  USA: {
    label: "United States", currency: "USD", gstRate: 0, gstEnabled: false, gstLabel: "Sales Tax",
    hoursPerWeek: 40, superPct: 3, workersCompPct: 2, leaveLoadingPct: 0,
    annualLeaveWeeks: 2, personalLeaveDays: 5, publicHolidayDays: 10, casualLoadingPct: 0,
  },
  CUSTOM: { label: "Custom", currency: null, gstRate: 0, gstEnabled: true, gstLabel: "Tax" },
};

export const DEFAULT_BUSINESS = {
  country: "AU",
  employmentType: "permanent", // permanent | casual
  baseHourlyRate: 32,
  hoursPerWeek: 38,
  superPct: 12, // superannuation on ordinary earnings
  workersCompPct: 2, // workers compensation (provisional)
  leaveLoadingPct: 17.5, // annual-leave loading, applied to the 4 weeks' leave pay
  annualLeaveWeeks: 4,
  personalLeaveDays: 10, // personal / sick leave
  publicHolidayDays: 11,
  casualLoadingPct: 25, // in lieu of paid leave (casuals)
  gstEnabled: true,
  gstRate: 10,
};

// Common laser hair-removal treatment areas with default pulse counts (editable)
export const TREATMENT_AREAS = [
  { name: "Upper Lip", pulses: 60 },
  { name: "Chin", pulses: 80 },
  { name: "Lip & Chin", pulses: 140 },
  { name: "Sides of Face", pulses: 200 },
  { name: "Full Face", pulses: 400 },
  { name: "Underarms", pulses: 200 },
  { name: "Half Arms", pulses: 700 },
  { name: "Full Arms", pulses: 1200 },
  { name: "Bikini Line", pulses: 300 },
  { name: "Brazilian", pulses: 600 },
  { name: "Half Legs", pulses: 1800 },
  { name: "Full Legs", pulses: 3500 },
  { name: "Chest", pulses: 1500 },
  { name: "Back", pulses: 2500 },
  { name: "Full Body", pulses: 8000 },
];

export const COMMON_CONSUMABLES = [
  { name: "Cooling gel", cost: 1.5 },
  { name: "Disposable eyewear", cost: 1.0 },
  { name: "Bed cover", cost: 1.5 },
  { name: "Gloves", cost: 0.6 },
  { name: "Skin prep swabs", cost: 0.8 },
  { name: "Numbing cream", cost: 2.5 },
  { name: "Post-treatment gel", cost: 1.2 },
];

const n = (v) => {
  const p = parseFloat(v);
  return isNaN(p) ? 0 : Math.max(0, p);
};

const DAYS_PER_WEEK = 5;

// Detailed true employment cost. Permanent employees account for on-costs AND
// reduced productive hours (paid leave). Casuals get a loading in lieu of leave.
export function employmentCost(business) {
  const base = n(business.baseHourlyRate);
  const hpw = n(business.hoursPerWeek) || 38;
  const superPct = n(business.superPct);
  const wcPct = n(business.workersCompPct);

  if (business.employmentType === "casual") {
    const loaded = base * (1 + n(business.casualLoadingPct) / 100);
    const trueHourly = loaded * (1 + (superPct + wcPct) / 100);
    return {
      mode: "casual",
      trueHourly,
      base,
      upliftPct: base > 0 ? (trueHourly / base - 1) * 100 : 0,
      productiveWeeks: 52,
      loadingAmount: loaded - base,
    };
  }

  // Permanent
  const annualBaseWage = base * hpw * 52;
  const superAmt = annualBaseWage * (superPct / 100);
  const wcAmt = annualBaseWage * (wcPct / 100);
  const leaveLoadingAmt = n(business.annualLeaveWeeks) * hpw * base * (n(business.leaveLoadingPct) / 100);
  const totalAnnual = annualBaseWage + superAmt + wcAmt + leaveLoadingAmt;

  const nonProductiveWeeks =
    n(business.annualLeaveWeeks) +
    n(business.personalLeaveDays) / DAYS_PER_WEEK +
    n(business.publicHolidayDays) / DAYS_PER_WEEK;
  const productiveWeeks = Math.max(1, 52 - nonProductiveWeeks);
  const productiveHours = productiveWeeks * hpw;
  const trueHourly = productiveHours > 0 ? totalAnnual / productiveHours : 0;

  return {
    mode: "permanent",
    trueHourly,
    base,
    upliftPct: base > 0 ? (trueHourly / base - 1) * 100 : 0,
    productiveWeeks,
    nonProductiveWeeks,
    totalAnnual,
  };
}

export function trueHourlyCost(business) {
  return employmentCost(business).trueHourly;
}

// Full treatment cost + profitable price
export function calcTreatment({ deviceParams, pulses, materials, timeHours, timeMinutes, margin, priceExGstOverride, business }) {
  const calc = calculate(deviceParams);
  const timeHrs = n(timeHours) + n(timeMinutes) / 60;
  // Flashlamp wear/servicing (per shot) + electricity (kW × price × runtime).
  const flashCost = calc.totalPerPulseExSundries * n(pulses);
  const elecCost = calc.elecPerHour * timeHrs;
  const laserCost = flashCost + elecCost; // total machine running cost for this treatment
  const materialsCost = (materials || []).reduce((s, m) => s + n(m.cost), 0);
  const hourly = trueHourlyCost(business);
  const timeCost = hourly * timeHrs;
  // Time-based overhead allocation (fixed monthly costs ÷ operating hours).
  const overheadPerHour = n(business.overheadPerHour);
  const overheadCost = overheadPerHour * timeHrs;
  // Major failure reserve for THIS device — time-based set-aside (repair ÷ years),
  // spread across the clinic's monthly operating hours and allocated by treatment time.
  const operatingHours = n(business.operatingHoursPerMonth);
  const monthlyReserve = n(calc.monthlyReserve);
  const reservePerHour = operatingHours > 0 ? monthlyReserve / operatingHours : 0;
  const reserveCost = reservePerHour * timeHrs;

  const breakEven = laserCost + materialsCost + timeCost + overheadCost + reserveCost;
  const gstRate = business.gstEnabled ? n(business.gstRate) : 0;

  // Either derive price from a target margin, OR use a manually entered price (ex-GST)
  // and back-calculate the resulting margin. Admin sets one, the other follows.
  const hasManualPrice = priceExGstOverride !== undefined && priceExGstOverride !== null && priceExGstOverride !== "" && !isNaN(parseFloat(priceExGstOverride));
  let priceExGst;
  if (hasManualPrice) {
    priceExGst = Math.max(0, parseFloat(priceExGstOverride));
  } else {
    const m = n(margin);
    priceExGst = m < 100 ? breakEven / (1 - m / 100) : breakEven;
  }
  const gstAmount = priceExGst * (gstRate / 100);
  const priceInclGst = priceExGst + gstAmount;
  const profit = priceExGst - breakEven;
  const marginPct = priceExGst > 0 ? (profit / priceExGst) * 100 : 0;

  return { perPulse: calc.totalPerPulseExSundries, flashCost, elecCost, laserCost, materialsCost, timeHrs, hourly, timeCost, overheadPerHour, overheadCost, monthlyReserve, reservePerHour, reserveCost, breakEven, priceExGst, gstAmount, priceInclGst, profit, marginPct };
}
